/**
 * [#663] Tenant Lead Portal — the portal SECTION of the existing shell nav.
 *
 * This is a section inside the shell already in use (NuShell rail + toolbar +
 * tokens), NOT a second shell and NOT a second visual language (design brief
 * §2). It reuses `ShellNavGroup` / `ShellNavItem` verbatim so the rail renders
 * portal entries with the same component, icons, and active-state handling as
 * every other nav entry.
 *
 * Filtering axis: the PORTAL nav is filtered by the per-tenant PAGE GRANT
 * matrix (`GrantMatrix`, #662), not by the role matrix that filters the main
 * nav. A tenant granted only Deliveries sees only Deliveries. Grants with
 * access 'none' are absent from the rail entirely.
 *
 * Filtering is VISIBILITY, never the security boundary — every portal route
 * is enforced server-side by `requirePortalGrant`
 * (src/portal/permissions/portalGrant.middleware.ts). A user who types an
 * ungranted path reaches the designed permission-denied state, and the data
 * behind it is refused by the server regardless.
 *
 * Route prefix is `/lead-portal` deliberately: epic #669 records that the
 * pre-existing `#/portal/*` surface is the API-RESELLER portal, a different
 * product area. Distinct prefixes keep the two from being conflated.
 */

import type { ShellNavGroup, ShellNavItem } from "./navConfig";
import {
  GRANTABLE_PAGE_AREAS,
  type GrantablePageArea,
  type GrantMatrix,
} from "../../portal/permissions/pageAreas";

export const PORTAL_ROUTE_PREFIX = "/lead-portal";

export interface PortalNavItem extends ShellNavItem {
  /** The page-area grant that makes this entry visible AND its data readable. */
  pageArea: GrantablePageArea;
}

/** A `ShellNavGroup` whose items always carry their page area. */
export interface PortalNavGroup extends ShellNavGroup {
  items: readonly PortalNavItem[];
}

/**
 * One entry per grantable page area, in the epic's page order. `admin_matrix`
 * is intentionally absent: it is the internal NU-GUI surface, role-gated and
 * never grantable, so it can never appear in a tenant's portal nav.
 */
export const PORTAL_NAV_ITEMS: readonly PortalNavItem[] = [
  {
    id: "portal-home",
    pageArea: "portal_home",
    label: "Portal Home",
    icon: "dashboard",
    route: PORTAL_ROUTE_PREFIX,
  },
  {
    id: "portal-requests",
    pageArea: "lead_requests",
    label: "Lead Requests",
    icon: "list",
    route: `${PORTAL_ROUTE_PREFIX}/requests`,
  },
  {
    id: "portal-deliveries",
    pageArea: "deliveries",
    label: "Deliveries",
    icon: "export",
    route: `${PORTAL_ROUTE_PREFIX}/deliveries`,
  },
  {
    id: "portal-reports",
    pageArea: "results_reports",
    label: "Results Reports",
    icon: "report",
    route: `${PORTAL_ROUTE_PREFIX}/reports`,
  },
  {
    id: "portal-returns",
    pageArea: "optout_returns",
    label: "Opt-Out Returns",
    icon: "refresh",
    route: `${PORTAL_ROUTE_PREFIX}/returns`,
  },
  {
    id: "portal-complaints",
    pageArea: "complaints",
    label: "Complaints",
    icon: "feedback",
    route: `${PORTAL_ROUTE_PREFIX}/complaints`,
  },
];

const ITEM_BY_AREA: Readonly<Record<GrantablePageArea, PortalNavItem>> =
  Object.fromEntries(
    PORTAL_NAV_ITEMS.map((item) => [item.pageArea, item]),
  ) as Record<GrantablePageArea, PortalNavItem>;

/**
 * The page area a portal path belongs to, or null when the path is not a
 * portal area at all. Sub-paths resolve to their area (a delivery detail page
 * is still Deliveries); the portal root matches EXACTLY, so an unknown
 * `/lead-portal/<something>` is not silently treated as Portal Home — it is
 * "no area", which the layout answers with the denied state.
 */
export function pageAreaForRoute(path: string): GrantablePageArea | null {
  const normalized = path.replace(/\/+$/, "") || PORTAL_ROUTE_PREFIX;
  let best: PortalNavItem | null = null;
  for (const item of PORTAL_NAV_ITEMS) {
    const isRoot = item.route === PORTAL_ROUTE_PREFIX;
    const matches = isRoot
      ? normalized === item.route
      : normalized === item.route || normalized.startsWith(`${item.route}/`);
    if (matches && (!best || item.route.length > best.route.length)) {
      best = item;
    }
  }
  return best ? best.pageArea : null;
}

/** True when the tenant holds any access ('read' or 'write') to `area`. */
export function hasGrant(
  grants: GrantMatrix | null | undefined,
  area: GrantablePageArea,
): boolean {
  if (!grants) return false;
  const access = grants[area];
  return access === "read" || access === "write";
}

/** The granted page areas, in the canonical page order. Fail-closed. */
export function grantedPageAreas(
  grants: GrantMatrix | null | undefined,
): readonly GrantablePageArea[] {
  return GRANTABLE_PAGE_AREAS.filter((area) => hasGrant(grants, area));
}

/**
 * The portal nav a tenant may SEE: exactly the granted areas, nothing else.
 * A tenant with no grants gets an empty group list — the shell then renders
 * the designed denied state rather than an empty rail.
 */
export function portalNavForGrants(
  grants: GrantMatrix | null | undefined,
): readonly PortalNavGroup[] {
  const items = grantedPageAreas(grants).map((area) => ITEM_BY_AREA[area]);
  if (items.length === 0) return [];
  return [{ id: "lead-portal", label: "Lead Portal", items }];
}

/** Landing route for a tenant: Portal Home when granted, else its first area. */
export function portalHomeRouteFor(
  grants: GrantMatrix | null | undefined,
): string | null {
  const areas = grantedPageAreas(grants);
  if (areas.length === 0) return null;
  return ITEM_BY_AREA[areas[0]].route;
}
